import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Brain, MessageSquare, Eye, Target, Zap, ArrowRight, CheckCircle, 
  Play, Star, TrendingUp, Bot, Lightbulb, Settings, Clock, Shield,
  BarChart3, Users, FileText, Workflow, ChevronRight, Code
} from 'lucide-react';

const AIIntelligence = () => {
  const [activeDemo, setActiveDemo] = useState('conversation');

  const aiCapabilities = [
    {
      id: 'conversation',
      name: 'Conversational AI',
      icon: MessageSquare,
      description: 'Natural language interface for data queries and system operations',
      features: [
        'Ask questions in plain English about your business data',
        'Execute complex operations through natural conversation',
        'Context-aware responses with conversation history',
        'Multi-turn conversations for complex analysis'
      ],
      apiEndpoints: [
        { name: 'askQuestion', description: 'Process natural language queries' },
        { name: 'processQuery', description: 'Handle conversational interactions' },
        { name: 'createConversation', description: 'Start new AI conversations' },
        { name: 'listConversations', description: 'Manage conversation history' }
      ],
      demoQueries: [
        '"Show me our top 10 customers by revenue this quarter"',
        '"Create a sales report for the last 30 days"',
        '"Which products have the highest return rates?"',
        '"Generate an invoice for customer ABC Corp"'
      ]
    },
    {
      id: 'insights',
      name: 'Smart Analytics & Insights',
      icon: Eye,
      description: 'AI-powered data analysis that identifies patterns and opportunities',
      features: [
        'Automated insight generation from business data',
        'Anomaly detection and trend identification',
        'Predictive analytics and forecasting',
        'Performance recommendations and optimization'
      ],
      apiEndpoints: [
        { name: 'getInsights', description: 'Generate AI-powered insights' },
        { name: 'getOperationHistory', description: 'Track AI operation patterns' }
      ],
      demoQueries: [
        'Identify declining customer satisfaction trends',
        'Predict inventory shortages before they occur',
        'Recommend process optimizations based on data',
        'Detect unusual spending patterns automatically'
      ]
    },
    {
      id: 'autonomous',
      name: 'Autonomous Operations',
      icon: Zap,
      description: 'AI that performs operations with configurable autonomy levels',
      features: [
        'Cautious mode: Ask before executing any operation',
        'Balanced mode: Execute with confirmation prompts',
        'Autonomous mode: Full automation with oversight',
        'Complete undo capabilities for all AI operations'
      ],
      apiEndpoints: [
        { name: 'setAutonomyLevel', description: 'Configure AI autonomy settings' },
        { name: 'undoOperation', description: 'Reverse AI-performed operations' },
        { name: 'getUndoableOperations', description: 'List reversible operations' }
      ],
      demoQueries: [
        'Automatically update customer records based on conditions',
        'Generate and distribute reports on schedule',
        'Manage data cleanup and maintenance tasks',
        'Execute workflow triggers based on business rules'
      ]
    }
  ];

  const useCases = [
    {
      industry: 'Sales & Marketing',
      icon: TrendingUp,
      scenarios: [
        'Ask "Which leads are most likely to convert?" and get AI-powered scoring',
        'Automatically identify upselling opportunities in customer data',
        'Generate personalized marketing reports through conversation',
        'Predict campaign performance before launch'
      ]
    },
    {
      industry: 'Operations',
      icon: Settings,
      scenarios: [
        'Query "What processes are causing delays?" for operational insights',
        'Automatically detect and flag quality issues in production',
        'Generate efficiency reports through natural language requests',
        'Predict maintenance needs before equipment fails'
      ]
    },
    {
      industry: 'Finance',
      icon: BarChart3,
      scenarios: [
        'Ask "Show me cash flow projections" for instant financial analysis',
        'Automatically detect unusual spending patterns or fraud',
        'Generate compliance reports through conversational interface',
        'Predict budget variances and recommend adjustments'
      ]
    },
    {
      industry: 'Human Resources',
      icon: Users,
      scenarios: [
        'Query "Which departments have high turnover?" for HR insights',
        'Automatically identify training needs across the organization',
        'Generate employee performance reports conversationally',
        'Predict hiring needs based on growth patterns'
      ]
    }
  ];

  const testimonials = [
    {
      quote: "The conversational AI has transformed how our team accesses data. Instead of complex queries, we just ask questions naturally.",
      name: "",
      title: "Data Analytics Manager",
      company: "RetailCorp",
      avatar: "👨‍💼",
      metric: "75% faster data access"
    },
    {
      quote: "AI insights helped us identify a customer churn pattern we never would have spotted manually. Saved us $2M in revenue.",
      name: "",
      title: "VP of Customer Success",
      company: "SaaS Innovations",
      avatar: "👩‍💼",
      metric: "$2M revenue saved"
    },
    {
      quote: "Autonomous operations handle our routine data management, freeing our team to focus on strategic initiatives.",
      name: "",
      title: "Operations Director",
      company: "Manufacturing Plus",
      avatar: "👨‍💻",
      metric: "60% time savings"
    }
  ];

  const stats = [
    { number: '95%', label: 'Query Accuracy', description: 'Natural language understanding' },
    { number: '10x', label: 'Faster Insights', description: 'Compared to manual analysis' },
    { number: '24/7', label: 'AI Availability', description: 'Round-the-clock intelligence' },
    { number: '99.9%', label: 'Uptime SLA', description: 'Reliable AI operations' }
  ];

  const securityFeatures = [
    { name: 'Data Privacy', description: 'AI processes data without storing sensitive information' },
    { name: 'Role-Based Access', description: 'AI respects user permissions and access controls' },
    { name: 'Audit Trails', description: 'Complete logging of all AI operations and decisions' },
    { name: 'Undo Capabilities', description: 'Reverse any AI operation with full rollback' }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 overflow-hidden">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center lg:text-left text-white"
            >
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 text-white mb-6">
              <Brain className="h-10 w-10" />
            </div>
            <h1 className="text-5xl lg:text-6xl font-bold mb-6">
              AI & Intelligence
              <span className="block text-3xl lg:text-4xl font-normal mt-2 text-purple-200">
                Conversational Business Intelligence
              </span>
            </h1>
            <p className="text-xl lg:text-2xl text-white/80 mb-10 leading-relaxed">
              Transform how your organization accesses insights with AI that understands natural language, 
              generates smart recommendations, and operates autonomously to streamline your business.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.a
                href="https://app.aquargin.com/register"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="bg-white text-gray-900 px-8 py-4 rounded-lg font-semibold text-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center group"
              >
                Try AI Features Free
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </motion.a>
              
              <motion.a
                href="https://www.youtube.com/@nocodeAquargin"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-white hover:text-gray-900 transition-colors duration-300 flex items-center justify-center group"
              >
                <Play className="mr-2 h-5 w-5" />
                Watch AI Demo
              </motion.a>
            </div>
          </motion.div>
          
          {/* AI Visualization */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            className="relative"
          >
            <img
              src="/images/Abstract_3D_visualization_of_AI_brain_made_of_inte_1750595474.png"
              alt="AI Brain Neural Network Visualization"
              className="w-full h-auto rounded-2xl shadow-2xl"
            />
          </motion.div>
        </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center bg-white p-6 rounded-xl shadow-lg"
              >
                <div className="text-4xl font-bold text-purple-600 mb-2">{stat.number}</div>
                <div className="text-gray-900 font-semibold mb-1">{stat.label}</div>
                <div className="text-sm text-gray-600">{stat.description}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* AI Capabilities Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              AI Capabilities
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Three powerful AI systems working together to transform your business intelligence
            </p>
          </div>

          {/* Capability Tabs */}
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {aiCapabilities.map((capability) => (
              <button
                key={capability.id}
                onClick={() => setActiveDemo(capability.id)}
                className={`flex items-center space-x-3 px-6 py-3 rounded-lg font-semibold transition-all duration-300 ${
                  activeDemo === capability.id
                    ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <capability.icon className="h-5 w-5" />
                <span>{capability.name}</span>
              </button>
            ))}
          </div>

          {/* AI Capability Images */}
          <div className="grid lg:grid-cols-3 gap-8 mb-16">
            <div className="text-center">
              <img
                src="/images/Split-screen_composition_showing_professional_woma_1750595443.png"
                alt="Conversational AI Interface"
                className="w-full h-48 object-cover rounded-lg shadow-lg mb-4"
              />
              <h3 className="text-lg font-semibold text-gray-900">Conversational AI</h3>
            </div>
            <div className="text-center">
              <img
                src="/images/Large_wall-mounted_display_showing_real-time_analy_1750595506.png"
                alt="Smart Analytics Dashboard"
                className="w-full h-48 object-cover rounded-lg shadow-lg mb-4"
              />
              <h3 className="text-lg font-semibold text-gray-900">Smart Analytics</h3>
            </div>
            <div className="text-center">
              <img
                src="/images/Visual_representation_of_automated_triggers_and_co_1750596720.png"
                alt="Autonomous Operations"
                className="w-full h-48 object-cover rounded-lg shadow-lg mb-4"
              />
              <h3 className="text-lg font-semibold text-gray-900">Autonomous Operations</h3>
            </div>
          </div>

          {/* Active Capability Content */}
          {aiCapabilities.map((capability) => (
            activeDemo === capability.id && (
              <motion.div
                key={capability.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="max-w-6xl mx-auto"
              >
                <div className="grid lg:grid-cols-2 gap-12 items-center">
                  <div>
                    <div className="flex items-center mb-6">
                      <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg flex items-center justify-center mr-4">
                        <capability.icon className="h-6 w-6 text-white" />
                      </div>
                      <h3 className="text-3xl font-bold text-gray-900">{capability.name}</h3>
                    </div>
                    <p className="text-xl text-gray-600 mb-8">{capability.description}</p>
                    
                    <div className="space-y-4">
                      <h4 className="text-lg font-semibold text-gray-900 flex items-center">
                        <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                        Key Features
                      </h4>
                      <ul className="space-y-3">
                        {capability.features.map((feature, idx) => (
                          <li key={idx} className="flex items-start space-x-3">
                            <div className="w-2 h-2 bg-purple-500 rounded-full mt-2 flex-shrink-0"></div>
                            <span className="text-gray-700">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  <div className="bg-gray-50 p-8 rounded-2xl">
                    <h4 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
                      <Bot className="h-5 w-5 text-purple-500 mr-2" />
                      Example Interactions
                    </h4>
                    <div className="space-y-4">
                      {capability.demoQueries.map((query, idx) => (
                        <div key={idx} className="bg-white p-4 rounded-lg border-l-4 border-purple-500">
                          <div className="flex items-start space-x-3">
                            <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                              <MessageSquare className="h-4 w-4 text-purple-600" />
                            </div>
                            <div>
                              <p className="text-gray-800 font-medium">{query}</p>
                              {idx === 0 && (
                                <p className="text-sm text-gray-500 mt-1">
                                  AI responds with interactive data visualization and insights
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="mt-8 pt-6 border-t border-gray-200">
                      <h5 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
                        <Code className="h-4 w-4 text-purple-500 mr-2" />
                        API Endpoints
                      </h5>
                      <div className="space-y-2">
                        {capability.apiEndpoints.map((endpoint, idx) => (
                          <div key={idx} className="flex items-center justify-between">
                            <span className="text-sm font-mono bg-gray-200 px-2 py-1 rounded">
                              {endpoint.name}
                            </span>
                            <span className="text-xs text-gray-600">{endpoint.description}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            )
          ))}
        </div>
      </section>

      {/* Use Cases by Industry */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              AI Across Industries
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              See how different departments leverage AI intelligence for their specific needs
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {useCases.map((useCase, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-shadow"
              >
                <div className="flex items-center mb-6">
                  <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg flex items-center justify-center mr-4">
                    <useCase.icon className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">{useCase.industry}</h3>
                </div>
                
                <div className="space-y-4">
                  {useCase.scenarios.map((scenario, idx) => (
                    <div key={idx} className="flex items-start space-x-3">
                      <ChevronRight className="h-5 w-5 text-purple-500 mt-0.5 flex-shrink-0" />
                      <p className="text-gray-700">{scenario}</p>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Security & Trust */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center mb-16">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-r from-green-500 to-blue-500 text-white mb-6">
              <Shield className="h-8 w-8" />
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Secure & Trustworthy AI
            </h2>
            <p className="text-xl text-gray-600">
              Enterprise-grade security and transparency in every AI operation
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {securityFeatures.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="flex items-start space-x-4 p-6 bg-gray-50 rounded-xl"
              >
                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{feature.name}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              AI Success Stories
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Real results from organizations using Aquargin's AI capabilities
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-shadow"
              >
                <div className="flex items-center mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-5 w-5 text-yellow-400 fill-current" />
                  ))}
                </div>
                
                <blockquote className="text-gray-700 mb-6 text-lg leading-relaxed">
                  "{testimonial.quote}"
                </blockquote>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="text-3xl mr-3">{testimonial.avatar}</div>
                    <div>
                      <div className="font-bold text-gray-900">{testimonial.name}</div>
                      <div className="text-sm text-gray-600">{testimonial.title}</div>
                      <div className="text-sm text-gray-500">{testimonial.company}</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-purple-600">{testimonial.metric}</div>
                    <div className="text-xs text-gray-500">Result</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-purple-600 to-pink-600 relative overflow-hidden">
        <div className="absolute inset-0 bg-black opacity-10"></div>
        <div className="container mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center max-w-4xl mx-auto text-white"
          >
            <h2 className="text-4xl lg:text-5xl font-bold mb-6">
              Experience AI-Powered Intelligence
            </h2>
            <p className="text-xl mb-10 opacity-90">
              Start your free trial and discover how conversational AI can transform 
              your business intelligence and operations.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.a
                href="https://app.aquargin.com/register"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-white text-gray-900 px-8 py-4 rounded-lg font-bold text-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center group"
              >
                Start AI Trial Free
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </motion.a>
              
              <motion.a
                href="/contact"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="border-2 border-white text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-white hover:text-gray-900 transition-colors duration-300 flex items-center justify-center group"
              >
                <MessageSquare className="mr-2 h-5 w-5" />
                Schedule AI Demo
              </motion.a>
            </div>

            <div className="mt-8 text-white/80 text-sm">
              <p>✓ No credit card required  ✓ Full AI access  ✓ 24/7 support included</p>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AIIntelligence; 